import { db } from './firebase';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { PortalModelWalkthrough } from '../types';

function fromFirestore(
  snap: { id: string; data: () => Record<string, unknown> },
): PortalModelWalkthrough {
  const d = snap.data();
  const ts = (v: unknown): Date | undefined =>
    v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: () => Date }).toDate === 'function'
      ? (v as { toDate: () => Date }).toDate()
      : undefined;
  return {
    id: snap.id,
    portalId: String(d.portalId || ''),
    dealId: String(d.dealId || ''),
    releaseId: typeof d.releaseId === 'string' ? d.releaseId : undefined,
    bundleId: typeof d.bundleId === 'string' ? d.bundleId : undefined,
    storagePath: String(d.storagePath || ''),
    downloadUrl: String(d.downloadUrl || ''),
    durationSeconds: Number(d.durationSeconds) || 0,
    status: (['uploading', 'processing', 'ready', 'failed'] as const).includes(d.status as PortalModelWalkthrough['status'])
      ? (d.status as PortalModelWalkthrough['status'])
      : 'uploading',
    authorUid: String(d.authorUid || ''),
    authorName: String(d.authorName || ''),
    createdAt: ts(d.createdAt) || new Date(),
    transcript: typeof d.transcript === 'string' ? d.transcript : undefined,
    summary: typeof d.summary === 'string' ? d.summary : undefined,
    keyQuestions: Array.isArray(d.keyQuestions) ? d.keyQuestions.map(String) : undefined,
    keyConcerns: Array.isArray(d.keyConcerns) ? d.keyConcerns.map(String) : undefined,
    processedAt: ts(d.processedAt),
    error: typeof d.error === 'string' ? d.error : undefined,
  };
}

export function subscribePortalModelWalkthroughs(
  portalId: string,
  dealId: string,
  callback: (rows: PortalModelWalkthrough[]) => void,
): () => void {
  const q = query(
    collection(db, 'clientPortals', portalId, 'modelWalkthroughs'),
    where('dealId', '==', dealId),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }))),
    err => console.warn('[modelWalkthroughs] subscribe failed', err),
  );
}

export interface FinalizePortalWalkthroughInput {
  portalId: string;
  dealId: string;
  walkthroughId: string;
  storagePath: string;
  downloadUrl: string;
  durationSeconds: number;
  releaseId?: string;
  bundleId?: string;
  authorName: string;
}

export async function finalizePortalWalkthrough(
  input: FinalizePortalWalkthroughInput,
): Promise<{ ok: boolean; walkthroughId: string }> {
  const fn = httpsCallable<FinalizePortalWalkthroughInput, { ok: boolean; walkthroughId: string }>(
    getFunctions(undefined, 'us-central1'),
    'finalizePortalWalkthrough',
  );
  const res = await fn(input);
  return res.data;
}

export async function retryPortalWalkthroughProcessing(
  portalId: string,
  walkthroughId: string,
): Promise<{ ok: boolean }> {
  const fn = httpsCallable<{ portalId: string; walkthroughId: string }, { ok: boolean }>(
    getFunctions(undefined, 'us-central1'),
    'retryPortalWalkthroughProcessing',
  );
  const res = await fn({ portalId, walkthroughId });
  return res.data;
}

export function walkthroughStoragePath(
  portalId: string,
  dealId: string,
  walkthroughId: string,
  ext = 'webm',
): string {
  return `portal-files/${portalId}/walkthroughs/${dealId}/${walkthroughId}.${ext}`;
}

export function newWalkthroughId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function walkthroughDocRef(portalId: string, walkthroughId: string) {
  return doc(db, 'clientPortals', portalId, 'modelWalkthroughs', walkthroughId);
}

export async function createUploadingWalkthroughDoc(
  portalId: string,
  walkthroughId: string,
  data: Omit<PortalModelWalkthrough, 'id' | 'createdAt' | 'status'> & { status?: 'uploading' },
): Promise<void> {
  const { setDoc } = await import('firebase/firestore');
  await setDoc(walkthroughDocRef(portalId, walkthroughId), {
    ...data,
    status: 'uploading',
    createdAt: Timestamp.now(),
  });
}
